import { useEffect, useRef, useState } from "react";

import type { CalendarFilterState, FilterGroup } from "./use-calendar-filters";
import { LocationBadge } from "./location-badge";
import styles from "./filter-panel.module.css";

/** One choice in a section: a value, how it reads, and how many bookings it has. */
export interface FilterOption {
  value: string;
  label: string;
  /** Bookings in view with this value; null where the view does not count (a month). */
  count: number | null;
  /** A swatch, a circle or a tag before the label. */
  pre?: React.ReactNode;
  /** For a chair: the location it belongs to. */
  branchId?: string;
}

export interface FilterLocation {
  id: string;
  name: string;
  code: string;
  hue: number;
}

const TITLES: Record<FilterGroup, string> = {
  branchIds: "Locations",
  providerIds: "Doctors",
  chairIds: "Chairs",
  types: "Appointment type",
  statuses: "Status",
};

/** Past this many doctors the list gets a search box, and shows this many. */
const DOCTOR_LIMIT = 8;

/**
 * **The filters, as a bar of chips above the calendar** (CZ-D7, Emile,
 * 2026-10-21, superseding CF-D16's docked panel, which he found ugly and which
 * took the place the waitlist now has): location, doctor, chair, appointment
 * type and status, each a chip that opens its own dropdown of real checkboxes
 * with counts and an **Only** on every option. A chip with something hidden
 * says where it stands ("1 of 17"); Reset is offered per group and for all.
 * One dropdown at a time; a click elsewhere or Escape closes it.
 */
export function FilterBar({
  id,
  state,
  options,
  locations,
  doctorSignedIn,
}: {
  id: string;
  state: CalendarFilterState;
  options: Record<FilterGroup, FilterOption[]>;
  /** The locations shown; the Locations section and the chair rows need two or more. */
  locations: readonly FilterLocation[];
  /** A clinician signed in: the Doctors section offers "Only my bookings" (CF-D11). */
  doctorSignedIn: boolean;
}): React.JSX.Element {
  const [doctorQuery, setDoctorQuery] = useState("");
  const bar = useRef<HTMLDivElement | null>(null);
  const { openSection, setOpenSection } = state;

  // A click outside the bar, or Escape, closes the open dropdown; Escape hands
  // focus back to its chip.
  useEffect(() => {
    if (openSection === null) return;
    const outside = (event: PointerEvent): void => {
      if (!(event.target instanceof Node) || bar.current?.contains(event.target) !== true)
        setOpenSection(null);
    };
    const escape = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      setOpenSection(null);
      bar.current?.querySelector<HTMLButtonElement>(`[data-group="${openSection}"]`)?.focus();
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [openSection, setOpenSection]);
  const combined = locations.length > 1;
  const groups = (["branchIds", "providerIds", "chairIds", "types", "statuses"] as const).filter(
    (group) => group !== "branchIds" || combined,
  );

  const summaryOf = (group: FilterGroup): string => {
    if (group === "providerIds" && state.onlyMe) return "Only mine";
    const all = options[group];
    const off = all.filter((option) => state.isHidden(group, option.value)).length;
    return off === 0
      ? `All ${String(all.length)}`
      : `${String(all.length - off)} of ${String(all.length)}`;
  };
  const changed = (group: FilterGroup): boolean =>
    state.active.includes(group) || (group === "providerIds" && state.onlyMe);

  const row = (group: FilterGroup, option: FilterOption): React.JSX.Element => {
    const inputId = `${id}-${group}-${option.value}`;
    const all = options[group].map((each) => each.value);
    return (
      <li key={option.value} className={styles.option}>
        <input
          type="checkbox"
          id={inputId}
          checked={!state.isHidden(group, option.value)}
          onChange={(event) => state.toggle(group, option.value, event.target.checked)}
          data-testid="filter-option"
          data-group={group}
          data-value={option.value}
        />
        <label htmlFor={inputId}>
          {option.pre}
          <span className={styles.label}>{option.label}</span>
          {option.count !== null && (
            <span className={styles.count} aria-label={`${String(option.count)} appointments`}>
              {option.count}
            </span>
          )}
        </label>
        <button
          type="button"
          className={styles.only}
          onClick={() => state.only(group, option.value, all)}
          aria-label={`Show only ${option.label}`}
          data-testid="filter-only"
        >
          Only
        </button>
      </li>
    );
  };

  const body = (group: FilterGroup): React.ReactNode => {
    if (group === "chairIds" && combined) {
      // Chairs by location, each chair a small toggle: twenty chairs still fit.
      return (
        <div
          className={locations.length > 5 ? `${styles.chairs} ${styles.chairsTwo}` : styles.chairs}
        >
          {locations.map((location) => (
            <div key={location.id} className={styles.chairRow}>
              <span className={styles.chairLocation}>
                <LocationBadge code={location.code} hue={location.hue} name={location.name} />
                <span className={styles.chairLocationName}>{location.name}</span>
              </span>
              <span className={styles.chairButtons}>
                {options.chairIds
                  .filter((option) => option.branchId === location.id)
                  .map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={styles.chip}
                      aria-pressed={!state.isHidden("chairIds", option.value)}
                      aria-label={`${location.name} ${option.label}${
                        option.count === null ? "" : `, ${String(option.count)} appointments`
                      }`}
                      title={`${location.name} · ${option.label}`}
                      onClick={() =>
                        state.toggle(
                          "chairIds",
                          option.value,
                          state.isHidden("chairIds", option.value),
                        )
                      }
                      data-testid="filter-chair"
                    >
                      {shortChair(option.label)}
                    </button>
                  ))}
              </span>
            </div>
          ))}
        </div>
      );
    }

    if (group === "providerIds") {
      const all = options.providerIds;
      const query = doctorQuery.trim().toLowerCase();
      const matching = all.filter(
        (option) => query === "" || option.label.toLowerCase().includes(query),
      );
      return (
        <>
          {doctorSignedIn && (
            <label className={styles.me}>
              <input
                type="checkbox"
                checked={state.onlyMe}
                onChange={(event) => state.setOnlyMe(event.target.checked)}
                data-testid="filter-only-me"
              />
              Only my bookings
            </label>
          )}
          {all.length > DOCTOR_LIMIT && (
            <label className={styles.search}>
              <span className={styles.srOnly}>Find a doctor</span>
              <input
                type="search"
                placeholder="Find a doctor"
                value={doctorQuery}
                onChange={(event) => setDoctorQuery(event.target.value)}
                autoComplete="off"
                data-testid="filter-doctor-search"
              />
            </label>
          )}
          <ul className={styles.list}>
            {matching.slice(0, DOCTOR_LIMIT).map((option) => row(group, option))}
          </ul>
          {matching.length > DOCTOR_LIMIT && (
            <p className={styles.more}>
              {matching.length - DOCTOR_LIMIT} more: type a name to find them.
            </p>
          )}
          {matching.length === 0 && all.length > 0 && (
            <p className={styles.more}>No doctor called that.</p>
          )}
        </>
      );
    }

    return <ul className={styles.list}>{options[group].map((option) => row(group, option))}</ul>;
  };

  return (
    <div
      className={styles.bar}
      ref={bar}
      role="group"
      aria-label="Filters"
      data-testid="filter-bar"
    >
      {groups.map((group) => {
        const open = state.openSection === group;
        return (
          <div key={group} className={styles.slot}>
            <button
              type="button"
              className={changed(group) ? `${styles.trigger} ${styles.triggerOn}` : styles.trigger}
              aria-expanded={open}
              aria-controls={`${id}-body-${group}`}
              onClick={() => state.setOpenSection(open ? null : group)}
              data-testid="filter-section"
              data-group={group}
            >
              {TITLES[group]}
              {changed(group) && <span className={styles.summary}>{summaryOf(group)}</span>}
              <i aria-hidden="true">▾</i>
            </button>
            {open && (
              <div
                className={styles.popover}
                id={`${id}-body-${group}`}
                role="group"
                aria-label={TITLES[group]}
                data-testid="filter-popover"
              >
                {body(group)}
                {changed(group) && (
                  <button
                    type="button"
                    className={styles.link}
                    onClick={() => {
                      state.reset(group);
                      if (group === "providerIds") state.setOnlyMe(false);
                    }}
                    data-testid="filter-reset"
                  >
                    Reset {TITLES[group].toLowerCase()}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
      {(state.active.length > 0 || state.onlyMe) && (
        <button
          type="button"
          className={styles.link}
          onClick={() => state.reset("all")}
          data-testid="filter-reset-all"
        >
          Reset all
        </button>
      )}
    </div>
  );
}

export { TITLES as FILTER_TITLES };

/** `Chair 2` → `2` on a toggle; any other name is kept whole, never guessed at. */
function shortChair(name: string): string {
  const match = /^chair\s*(.+)$/i.exec(name.trim());
  return match?.[1] ?? name;
}
